import { useQuery, useMutation } from '@tanstack/react-query'
import { api, BASE, getAuthHeaders } from '@/api/client'

interface MediaItem {
  id: string
  type: 'photo' | 'video'
  url: string
  thumbnail: string | null
  width?: number
  height?: number
  name?: string | null
  title?: string | null
  created_time?: string | null
  localPath?: string | null
  permalink?: string | null
}

interface MediaListResponse {
  photos: MediaItem[]
  videos: MediaItem[]
  totalPhotos: number
  totalVideos: number
}

export interface VideoFormat {
  format_id: string
  label: string
  ext: string
  width: number | null
  height: number | null
  filesize: number | null
  needsMerge: boolean
  directUrl?: string
}

export interface VideoInfo {
  title: string
  duration: number | null
  thumbnail: string | null
  formats: VideoFormat[]
}

export function useFbPageMedia(pageId: string | null, type: string) {
  return useQuery<MediaListResponse>({
    queryKey: ['fb-page-media', pageId, type],
    queryFn: () => api.get(`/media-download/list?pageId=${pageId}&type=${type}`),
    enabled: !!pageId,
  })
}

export function useIgMedia(igUserId: string | null, type: string) {
  return useQuery<MediaListResponse>({
    queryKey: ['ig-media', igUserId, type],
    queryFn: () => api.get(`/media-download/instagram?igUserId=${igUserId}&type=${type}`),
    enabled: !!igUserId,
  })
}

export function useVideoInfo() {
  return useMutation<VideoInfo, Error, string>({
    mutationFn: (url: string) => api.post('/media-download/video-info', { url }),
  })
}

export interface SaveToLibraryResult {
  ok: boolean
  image: {
    id: string
    url: string
    filename: string
    mediaType: string
    addedAt: number | null
  }
  folder: { id: string; name: string }
  sizeMB: number
}

export function useSaveVideoToLibrary() {
  return useMutation<SaveToLibraryResult, Error, {
    url: string
    formatId: string
    needsMerge: boolean
    folderId: string
    title?: string
    directUrl?: string
  }>({
    mutationFn: (params) =>
      api.post('/media-download/video-save', {
        url: params.url,
        format_id: params.formatId,
        needs_merge: params.needsMerge,
        folder_id: params.folderId,
        title: params.title || 'Video',
        ...(params.directUrl ? { direct_url: params.directUrl } : {}),
      }),
  })
}

export async function downloadVideo(url: string, formatId: string, needsMerge: boolean, directUrl?: string): Promise<void> {
  const resp = await fetch(`${BASE}/media-download/video-download`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({ url, format_id: formatId, needs_merge: needsMerge, ...(directUrl ? { direct_url: directUrl } : {}) }),
  })

  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: 'Download falhou' }))
    throw new Error(err.error || 'Download falhou')
  }

  const blob = await resp.blob()
  const disposition = resp.headers.get('Content-Disposition')
  const filename = disposition?.match(/filename="?(.+?)"?$/)?.[1] || `video_${formatId}.mp4`

  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = filename
  document.body.appendChild(a)
  a.click()
  setTimeout(() => { URL.revokeObjectURL(a.href); a.remove() }, 1000)
}

export type { MediaItem, MediaListResponse }
